import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ShoppingBag, Star, Leaf, Droplets, Heart } from "lucide-react";
import axios from "axios";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HomePage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addToCart } = useCart();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    fetchProducts();
    
    // Check for affiliate code in URL
    const ref = searchParams.get("ref");
    if (ref) {
      validateAffiliateCode(ref);
    }
  }, [searchParams]);

  const validateAffiliateCode = async (code) => {
    try {
      const response = await axios.get(`${API}/affiliate/validate/${code}`);
      if (response.data.valid) {
        localStorage.setItem("affiliateCode", code);
        toast.success(`Referred by ${response.data.affiliate_name}!`);
      }
    } catch (error) {
      console.error("Invalid affiliate code");
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      console.error("Failed to fetch products:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async (productId) => {
    if (!user) {
      toast.error("Please sign in to add items to cart");
      return;
    }
    await addToCart(productId);
  };

  return (
    <div>
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(https://images.pexels.com/photos/6724531/pexels-photo-6724531.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)`
          }}
        >
          <div className="absolute inset-0 bg-black/40" />
        </div>
        
        <div className="container-main relative z-10">
          <div className="max-w-2xl">
            <p className="text-caption text-secondary mb-4 animate-fade-in">
              Natural Spa Experience
            </p>
            <h1 className="text-h1 text-white mb-6 animate-fade-in animation-delay-100">
              Transform Your<br />
              <span className="italic">Bathroom</span> Into a Spa
            </h1>
            <p className="text-body-lg text-white/90 mb-8 animate-fade-in animation-delay-200">
              Discover the rejuvenating power of natural mud with our premium bath and soap products. 
              Detoxify, nourish, and glow from home.
            </p>
            <div className="flex flex-wrap gap-4 animate-fade-in animation-delay-300">
              <a href="#products">
                <Button className="btn-primary text-lg" data-testid="shop-now-btn">
                  Shop Now
                </Button>
              </a>
              <Link to="/register">
                <Button className="btn-secondary bg-white/10 border-white/30 text-white hover:bg-white/20" data-testid="join-btn">
                  Join Our Community
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="section-spacing bg-muted/30">
        <div className="container-main">
          <div className="text-center mb-16">
            <p className="text-caption text-primary mb-3">Why Choose Us</p>
            <h2 className="text-h2">The At Home Mud Bath Difference</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="card-feature text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Leaf className="w-8 h-8 text-primary" />
              </div>
              <h3 className="font-heading text-xl mb-3">100% Natural</h3>
              <p className="text-muted-foreground">
                Our products are made with pure, natural clay and organic ingredients. No harsh chemicals.
              </p>
            </div>
            
            <div className="card-feature text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Droplets className="w-8 h-8 text-primary" />
              </div>
              <h3 className="font-heading text-xl mb-3">Deep Detox</h3>
              <p className="text-muted-foreground">
                Rich in minerals that draw out impurities and toxins, leaving your skin refreshed and clean.
              </p>
            </div>
            
            <div className="card-feature text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Heart className="w-8 h-8 text-primary" />
              </div>
              <h3 className="font-heading text-xl mb-3">Self-Care Ritual</h3>
              <p className="text-muted-foreground">
                Create your own spa sanctuary at home. Perfect for relaxation and rejuvenation.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="section-spacing">
        <div className="container-main">
          <div className="text-center mb-16">
            <p className="text-caption text-primary mb-3">Our Collection</p>
            <h2 className="text-h2">Premium Mud Products</h2>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
              {[1, 2].map((i) => (
                <div key={i} className="animate-pulse">
                  <div className="bg-muted aspect-[4/5] rounded-lg mb-4" />
                  <div className="h-6 bg-muted rounded w-3/4 mb-2" />
                  <div className="h-4 bg-muted rounded w-1/4" />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
              {products.map((product) => (
                <div key={product.id} className="card-product group" data-testid={`product-card-${product.id}`}>
                  <Link to={`/products/${product.id}`}>
                    <div className="aspect-[4/5] overflow-hidden">
                      <img
                        src={product.image}
                        alt={product.name}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                    </div>
                  </Link>
                  <div className="p-6 md:p-8">
                    <Link to={`/products/${product.id}`}>
                      <h3 className="font-heading text-2xl mb-2 group-hover:text-primary transition-colors">
                        {product.name}
                      </h3>
                    </Link>
                    <p className="text-muted-foreground mb-4 line-clamp-2">
                      {product.description}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-2xl font-heading text-primary">
                        ${product.price.toFixed(2)}
                      </span>
                      <Button 
                        className="btn-primary"
                        onClick={() => handleAddToCart(product.id)}
                        data-testid={`add-to-cart-${product.id}`}
                      >
                        <ShoppingBag className="w-4 h-4 mr-2" />
                        Add to Cart
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-spacing bg-secondary/30">
        <div className="container-main text-center">
          <h2 className="text-h2 mb-4">Ready to Transform Your Skin?</h2>
          <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto mb-8">
            Join thousands of customers who have discovered the rejuvenating power of mud therapy.
          </p>
          <a href="#products">
            <Button className="btn-primary text-lg" data-testid="cta-shop-btn">
              Start Your Journey
            </Button>
          </a>
        </div>
      </section>
    </div>
  );
}
